import { z } from "zod";
import { AftersaleCaseCardData } from "../aftersales/case.routes.js";
import { ListingKind } from "../catalog/listing.js";
import { PriceRange } from "../catalog/public.routes.js";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Quantity } from "../common/quantity.js";
import { BusinessCard } from "../directory/contacts.routes.js";
import { defineRoute } from "../http/route.js";
import { ChangeOrderCardData } from "../trade/change-order.routes.js";
import { InquiryCardData } from "../trade/inquiry.routes.js";
import { OrderCardData } from "../trade/order.view.js";
import { QuoteCardData } from "../trade/quote.routes.js";
import { ShipmentCardData } from "../trade/shipment.routes.js";
/**
 * An action a card offers the viewer: the contract route it calls and the body to send. The label comes from the
 * client's resources (`chat.cards.<entityType>.actions.<id>`), and the route writes the same audit event as the
 * screen that owns it (IM-3).
 */
export const CardAction = z
    .object({
    id: z.string().regex(/^[a-z_]{2,40}$/),
    route: z.string().describe("The contract route id, such as `directory.contact_requests.create`"),
    body: z.record(z.string(), z.unknown()),
})
    .meta({ id: "CardAction" });
/** A product as its card shows it (IM-3, card 045): read afresh through the public views for every viewer. */
export const ProductCardData = z
    .object({
    slug: z.string().describe("The public page is `/products/<slug>` on the web"),
    name: LocalizedText,
    listingKind: ListingKind,
    image: z
        .object({ url: z.url(), width: z.number().int().positive() })
        .nullable()
        .describe("The first image's smallest rendition"),
    priceRange: PriceRange.nullable().describe("Null when inquiry-only"),
    inquiryOnly: z.boolean(),
    moq: z
        .object({ quantity: Quantity, unit: z.string() })
        .nullable()
        .describe("The lowest minimum order across the options"),
    shop: z.object({ slug: z.string(), name: LocalizedText }),
})
    .meta({ id: "ProductCardData" });
/** Card types so far; each module adds its own with its entity. */
export const CardView = z
    .discriminatedUnion("entityType", [
    z.object({
        entityType: z.literal("contact"),
        entityId: Id.describe("The membership whose business card this is"),
        version: z.number().int().nonnegative(),
        data: BusinessCard,
        actions: z.array(CardAction),
    }),
    z.object({
        entityType: z.literal("product"),
        entityId: Id.describe("The product"),
        version: z.number().int().nonnegative(),
        data: ProductCardData,
        actions: z.array(CardAction).describe("`open`: the public page; Inquire joins it in the trade stage"),
    }),
    z.object({
        entityType: z.literal("inquiry"),
        entityId: Id.describe("The inquiry"),
        version: z.number().int().nonnegative(),
        data: InquiryCardData,
        actions: z
            .array(CardAction)
            .describe("`quote` and `decline` for the shop, `withdraw` for the buyer, while they are allowed"),
    }),
    z.object({
        entityType: z.literal("quote"),
        entityId: Id.describe("The quote version"),
        version: z.number().int().nonnegative(),
        data: QuoteCardData,
        actions: z
            .array(CardAction)
            .describe("`accept` and `counter` for the buyer, on the latest valid version"),
    }),
    z.object({
        entityType: z.literal("order"),
        entityId: Id.describe("The order"),
        version: z.number().int().nonnegative(),
        data: OrderCardData,
        actions: z
            .array(CardAction)
            .describe("The order's actions for the viewer, as `GET /v1/orders/{orderId}` gives"),
    }),
    z.object({
        entityType: z.literal("shipment"),
        entityId: Id.describe("The shipment"),
        version: z.number().int().nonnegative(),
        data: ShipmentCardData,
        actions: z.array(CardAction).describe("`proof_of_delivery` for the shop until it is delivered"),
    }),
    z.object({
        entityType: z.literal("change_order"),
        entityId: Id.describe("The change order"),
        version: z.number().int().nonnegative(),
        data: ChangeOrderCardData,
        actions: z
            .array(CardAction)
            .describe("`accept` and `decline` for the other side, `withdraw` for the proposer"),
    }),
    z.object({
        entityType: z.literal("aftersale_case"),
        entityId: Id.describe("The after-sales case"),
        version: z.number().int().nonnegative(),
        data: AftersaleCaseCardData,
        actions: z.array(CardAction).describe("None yet: the card opens the case"),
    }),
])
    .meta({ id: "CardView" });
export const CardEntityType = z
    .enum(["contact", "product", "inquiry", "quote", "order", "shipment", "change_order", "aftersale_case"])
    .meta({ id: "CardEntityType" });
export const postCard = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.cards.post",
    method: "POST",
    path: "/v1/conversations/{conversationId}/cards",
    marks: ["tenant", "idem"],
    summary: "Post a card into a conversation; the poster must be able to read the entity (a business card: only one's own)",
    prd: ["IM-3"],
    request: {
        params: z.object({ conversationId: Id }),
        body: z.object({ entityType: CardEntityType, entityId: Id }),
    },
    responses: {
        201: {
            description: "The card as posted",
            schema: z.object({ messageId: z.string(), card: CardView }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "PROVIDER_UNAVAILABLE"],
});
export const getCard = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "conversation:create",
    id: "messaging.cards.get",
    method: "GET",
    path: "/v1/cards/{entityType}/{entityId}",
    marks: ["tenant"],
    summary: "A card read afresh for the caller, with the actions they may take; the posted message is never trusted",
    prd: ["IM-3"],
    request: { params: z.object({ entityType: CardEntityType, entityId: Id }) },
    responses: { 200: { description: "The card", schema: CardView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const cardRoutes = [postCard, getCard];
//# sourceMappingURL=card.routes.js.map